import { AnchorHTMLAttributes, ReactNode } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import cx from 'classnames';

export type TextLinkTone = 'primary' | 'info' | 'danger' | 'inherit';

interface CommonProps {
  /** `inherit` takes the colour of the sentence around it — a link inside a consent or a caption. */
  tone?: TextLinkTone;
  /** `always` inside running text; `hover` for a standalone «Смотреть записи». */
  underline?: 'always' | 'hover';
  /** Layout, and the type of a standalone link; inside a sentence the link takes the sentence's type. */
  className?: string;
  children: ReactNode;
}

type RouterLinkProps = CommonProps & Omit<LinkProps, 'className' | 'children'>;
type AnchorProps = CommonProps & { to?: never; href: string } & Omit<
    AnchorHTMLAttributes<HTMLAnchorElement>,
    'className' | 'children'
  >;

export type TextLinkProps = RouterLinkProps | AnchorProps;

const toneClasses: Record<TextLinkTone, string> = {
  primary: 'text-primary',
  info: 'text-info',
  danger: 'text-danger',
  inherit: '',
};

/** A link in text: `to` routes inside the app, `href` leaves it (mail, files, other sites). */
export const TextLink = ({ tone = 'primary', underline = 'always', className, ...rest }: TextLinkProps) => {
  const classes = cx(
    'rounded-chip focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current',
    toneClasses[tone],
    underline === 'always' ? 'underline' : 'hover:underline',
    className,
  );
  const rel = rest.target === '_blank' ? (rest.rel ?? 'noopener noreferrer') : rest.rel;

  if ('to' in rest && rest.to !== undefined) return <Link {...rest} rel={rel} className={classes} />;
  return <a {...(rest as AnchorProps)} rel={rel} className={classes} />;
};
